import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import LoginStatusIndicator from '@components/common/elements/LoginStatusIndicator';
import ListPage from '@components/common/layout/ListPage';
import { DATE_FORMAT_VALUE, DEFAULT_TABLE_ITEM_SIZE_LARGE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import usePiServerDeviceFormModal from '@modules/restaurant/Devices/tabs/piServer/PiServerDeviceFormModal';
import { convertUtcToLocalTime } from '@utils';
import { Button } from 'antd';


const message = defineMessages({
    objectName: 'Devices',
});

const PiServerDeviceListPage = ({ tabKey, restaurantId, restaurant, savedFilter, onFilterChange, handleGetServerByRestaurantId }) => {
    const translate = useTranslate();
    const queryParams = new URLSearchParams(location.search);
    const restaurantName = queryParams.get('restaurantName');
    const [fetchedAt, setFetchedAt] = React.useState(null);
    const { data, mixinFuncs, queryFilter, loading, pagination, pagePath } = useListBase({
        apiConfig: apiConfig.kitplayDevice,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },

        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    setFetchedAt(new Date());
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.getList = () => {
                const params = mixinFuncs.prepareGetListParams(queryFilter);
                mixinFuncs.handleFetchList({ 
                    ...params,
                    restaurantId,
                });
            };
            funcs.getCreateLink = () => {
                return `${pagePath}/pi-server/create?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}`;
            };
            funcs.getItemDetailLink = (dataRow) => {
                return `${pagePath}/pi-server/${dataRow.id}?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}`;
            };
            funcs.additionalActionColumnButtons = () => {
                return {
                    editItem: (record) => {
                        if (!mixinFuncs.hasPermission([apiConfig.kitplayDevice.update.baseURL])) return null;
                        return (
                            <BaseTooltip type="edit">
                                <Button
                                    type="link"
                                    style={{ padding: 0 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openEdit(record);
                                    }}
                                >
                                    <EditOutlined />
                                </Button>
                            </BaseTooltip>
                        );
                    },
                    loginStatus: (record) => {
                        return <LoginStatusIndicator lastTimeLogin={record.lastTimeLogin} fetchedAt={fetchedAt} translate={translate} />;
                    },
                };
            };
            const originalonDeleteItemCompleted = funcs.onDeleteItemCompleted;
            funcs.onDeleteItemCompleted = (id) => {
                originalonDeleteItemCompleted(id);
                handleGetServerByRestaurantId(restaurantId);
            };
        },
    });

    const { openCreate, openEdit, renderModals } = usePiServerDeviceFormModal({
        restaurantId,
        onReload: () => {
            mixinFuncs.getList();
            handleGetServerByRestaurantId(restaurantId);
        },
    });

    const columns = [
        {
            title: <FormattedMessage defaultMessage="Device name" />,
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: <FormattedMessage defaultMessage="Device ID" />,
            dataIndex: 'deviceId',
            width: 120,
        },
        {
            title: <FormattedMessage defaultMessage="Expiration date" />,
            width: 180,
            dataIndex: 'expireDate',
            align: 'right',
            render: (createdDate) => {
                const createdDateLocal = convertUtcToLocalTime(createdDate, DATE_FORMAT_VALUE, DATE_FORMAT_VALUE);
                return <div>{createdDateLocal}</div>;
            },
        },
        mixinFuncs.renderStatusColumn({ width: '120px' }),
        mixinFuncs.renderActionColumn(
            {
                editItem: mixinFuncs.hasPermission([apiConfig.kitplayDevice.update.baseURL]),
                delete: mixinFuncs.hasPermission([apiConfig.kitplayDevice.delete.baseURL]),
                loginStatus: true,
            },
            { width: '140px' },
        ),
    ];
    return (
        <>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ initialValues: queryFilter })}
                actionBar={
                    mixinFuncs.hasPermission([apiConfig.kitplayDevice.create.baseURL]) && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreate()} disabled={(data && data.length >= 1) || loading}>
                            </Button>
                        </div>
                    )
                }
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        pagination={pagination}
                    />
                }
            />
            {renderModals()}
        </>
    );
};

export default PiServerDeviceListPage;
